import React, { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Code2, Loader2, Play, Save } from 'lucide-react';
import { EXPR_FUNCTIONS } from './constants';

export function DeriveExpressionDialog({ open, onOpenChange, name, expression, sourceFields, onSave, steps, stepIndex }) {
  const [localName, setLocalName] = useState(name || '');
  const [localExpr, setLocalExpr] = useState(expression || '');
  const [activeCategory, setActiveCategory] = useState('Math');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testError, setTestError] = useState(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    setLocalName(name || '');
    setLocalExpr(expression || '');
    setTestResult(null);
    setTestError(null);
  }, [name, expression, open]);

  const insertAtCursor = (text) => {
    const el = textareaRef.current;
    if (!el) { setLocalExpr(prev => prev + text); return; }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const before = localExpr.substring(0, start);
    const after = localExpr.substring(end);
    const newExpr = before + text + after;
    setLocalExpr(newExpr);
    setTimeout(() => { el.focus(); el.selectionStart = el.selectionEnd = start + text.length; }, 0);
  };

  const handleTest = async () => {
    if (!localExpr.trim()) return;
    setTesting(true);
    setTestError(null);
    setTestResult(null);
    try {
      const priorSteps = (steps || []).slice(0, stepIndex);
      const testSteps = [...priorSteps, { type: 'derive', name: localName || '_test_col', expression: localExpr }, { type: 'limit', count: 5 }];
      const res = await api.executePrep(testSteps, 5);
      if (res.error) { setTestError(res.error); }
      else { setTestResult(res); }
    } catch (err) { setTestError(err.message); }
    finally { setTesting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Code2 className="w-4 h-4 text-primary" /> Derive Column Expression</DialogTitle></DialogHeader>
        <div className="flex-1 overflow-auto space-y-4 mt-2">
          <div>
            <Label className="text-xs text-muted-foreground">New Column Name</Label>
            <Input value={localName} onChange={e => setLocalName(e.target.value)} placeholder="e.g. profit_margin" className="rounded-lg bg-muted/50 border-border/50 mt-1 font-mono" data-testid="derive-col-name" />
          </div>

          <Separator className="bg-border/30" />

          <div>
            <Label className="text-xs text-muted-foreground mb-1.5 block">Expression</Label>
            <textarea
              ref={textareaRef}
              value={localExpr}
              onChange={e => setLocalExpr(e.target.value)}
              placeholder="e.g. round(field_a / field_b * 100, 2)"
              className="w-full h-28 rounded-xl bg-[#0d1117] border border-border/40 p-3 text-sm font-mono text-emerald-300 resize-none outline-none focus:ring-1 focus:ring-primary/50 placeholder:text-muted-foreground/30"
              spellCheck={false}
              data-testid="derive-expression-input"
            />
          </div>

          {sourceFields.length > 0 && (
            <div>
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5 block">Available Fields (click to insert)</Label>
              <div className="flex flex-wrap gap-1">
                {sourceFields.map(f => (
                  <button key={f} onClick={() => insertAtCursor(f)} className="px-2 py-1 rounded-md text-[10px] font-mono bg-primary/15 text-primary border border-primary/30 hover:bg-primary/25 transition-colors active:scale-95" data-testid={`insert-field-${f}`}>
                    {f}
                  </button>
                ))}
              </div>
            </div>
          )}

          <Separator className="bg-border/30" />

          <div>
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5 block">Functions</Label>
            <div className="flex gap-1 mb-2">
              {EXPR_FUNCTIONS.map(cat => (
                <button key={cat.cat} onClick={() => setActiveCategory(cat.cat)} className={`px-2.5 py-1 rounded-lg text-[10px] transition-colors ${activeCategory === cat.cat ? 'bg-primary/20 text-primary font-semibold' : 'text-muted-foreground hover:bg-muted/30'}`}>
                  {cat.cat}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-1">
              {(EXPR_FUNCTIONS.find(c => c.cat === activeCategory)?.fns || []).map(fn => (
                <button key={fn.name} onClick={() => insertAtCursor(fn.sig)} className="flex items-start gap-2 p-2 rounded-lg border border-border/20 hover:border-primary/30 hover:bg-primary/5 transition-all text-left active:scale-[0.98]" data-testid={`fn-${fn.name}`}>
                  <Code2 className="w-3 h-3 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-[10px] font-mono font-semibold text-foreground">{fn.sig}</p>
                    <p className="text-[9px] text-muted-foreground">{fn.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {(testResult || testError || testing) && (
          <div className="border-t border-border/30 pt-3 mt-2">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5 font-semibold">Test Results (5 rows)</p>
            {testing && <div className="flex items-center gap-2 py-3 justify-center"><Loader2 className="w-4 h-4 text-primary animate-spin" /><span className="text-xs text-muted-foreground">Running test...</span></div>}
            {testError && <div className="p-2.5 rounded-lg bg-destructive/10 border border-destructive/20 text-xs text-destructive">{testError}</div>}
            {testResult && testResult.data?.length > 0 && (() => {
              const cols = testResult.columns || Object.keys(testResult.data[0]);
              const derivedCol = localName || '_test_col';
              return (
                <div className="rounded-lg border border-border/30 overflow-auto max-h-44">
                  <table className="w-full text-[10px]">
                    <thead className="sticky top-0">
                      <tr className="border-b border-border/30">
                        {cols.map(c => (
                          <th key={c} className={'px-2 py-1.5 text-left font-semibold whitespace-nowrap ' + (c === derivedCol ? 'text-emerald-400 bg-emerald-500/10' : 'text-muted-foreground bg-muted/20')}>{c}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {testResult.data.map((row, i) => (
                        <tr key={i} className="border-b border-border/10">
                          {cols.map(c => (
                            <td key={c} className={'px-2 py-1 font-mono whitespace-nowrap ' + (c === derivedCol ? 'text-emerald-300 bg-emerald-500/5 font-semibold' : 'text-foreground/70')}>
                              {row[c] !== null && row[c] !== undefined ? String(row[c]) : <span className="text-muted-foreground/30">null</span>}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
            {testResult && (!testResult.data || testResult.data.length === 0) && <p className="text-[10px] text-muted-foreground text-center py-2">No data returned</p>}
          </div>
        )}

        <div className="flex gap-2 mt-4 pt-3 border-t border-border/30">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-lg flex-1">Cancel</Button>
          <Button variant="outline" onClick={handleTest} disabled={testing || !localExpr.trim()} className="rounded-lg" data-testid="test-derive-btn">
            {testing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-1.5" />} Test
          </Button>
          <Button onClick={() => { onSave(localName, localExpr); onOpenChange(false); }} disabled={!localName.trim() || !localExpr.trim()} className="rounded-lg bg-primary text-primary-foreground active:scale-95 flex-1" data-testid="save-derive-btn">
            <Save className="w-3.5 h-3.5 mr-1.5" /> Apply Expression
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
